import Phaser from "phaser";
import { mountDom } from "@/ui/domHelpers";
import type { CharCreatorCtx } from "../app/GameApp";
import { REGISTRY_KEYS } from "../app/GameApp";
import {
  CARD_CATALOG,
  CLASS_DEFS, CLASS_ORDER, DOMAIN_DEFS, describeApplyStatus,
  type CardDef, type CardEffect, type ClassId, type DiceNotation, type DomainId, type Stat, type Stats,
} from "../core/roscha";

const STAT_LABEL: Record<Stat, string> = {
  strength: "Сила",
  dexterity: "Ловк",
  intelligence: "Инт",
  wisdom: "Муд",
  constitution: "Тело",
};

const STAT_SHORT: Record<Stat, string> = {
  strength: "СИЛ",
  dexterity: "ЛОВ",
  intelligence: "ИНТ",
  wisdom: "МУД",
  constitution: "ТЕЛ",
};

const STAT_ORDER: Stat[] = ["strength", "dexterity", "intelligence", "wisdom", "constitution"];

const KIND_LABEL: Record<CardDef["kind"], string> = {
  attack: "Атака",
  spell: "Заклинание",
  action: "Действие",
};

function formula(base: number, dice?: DiceNotation, scale?: { stat: Stat; mult: number }): string {
  const parts: string[] = [];
  if (base > 0) parts.push(`<span class="cc__num">${base}</span>`);
  if (dice) {
    const min = dice.count;
    const max = dice.count * dice.sides;
    parts.push(`<span class="cc__dice">${dice.count}d${dice.sides}<span class="cc__dice-range">(${min}–${max})</span></span>`);
  }
  if (scale) {
    const statSpan = `<span class="cc__stat-ref">${STAT_SHORT[scale.stat]}</span>`;
    parts.push(scale.mult > 1 ? `<span class="cc__num">${scale.mult}</span>×${statSpan}` : statSpan);
  }
  return parts.join(' + ');
}

function renderEffect(e: CardEffect): string {
  switch (e.type) {
    case "damage":
      return `<span class="cc__formula-label">Урон</span> ${formula(e.base, e.dice, e.statScale)}`;
    case "heal":
      return `<span class="cc__formula-label">Лечение</span> ${formula(e.base, e.dice, e.statScale)}`;
    case "draw":
      return `<span class="cc__formula-label">Карты</span> <span class="cc__num">+${e.count}</span>`;
    case "bonusDraw":
      return `<span class="cc__formula-label">При крите</span> <span class="cc__num">+${e.count}</span> карт`;
    case "bonusAttack": {
      const scope = e.scope === "nextAttack" ? "след. атака" : "все атаки";
      return `<span class="cc__formula-label">${scope}</span> + <span class="cc__stat-ref">${STAT_SHORT[e.bonusFromStat]}</span>`;
    }
    case "armorDebuff": {
      if (e.value < 0) {
        const tag = e.onlyOnCrit ? "При крите" : "Армор";
        return `<span class="cc__formula-label">${tag}</span> <span class="cc__num">+${-e.value}</span>`;
      }
      const tag = e.onlyOnCrit ? "При крите" : "Армор цели";
      return `<span class="cc__formula-label">${tag}</span> <span class="cc__num">−${e.value}</span>`;
    }
    case "armorBuff":
      return `<span class="cc__formula-label">Армор</span> +${formula(e.base, e.dice, e.statScale)}`;
    case "selfDamage":
      return `<span class="cc__formula-label">Себе</span> −${formula(e.base, e.dice, e.statScale)} HP`;
    case "applyStatus":
      return `<span class="cc__formula-label">${describeApplyStatus(e)}</span>`;
  }
}

function renderEffects(effects: CardEffect[]): string {
  return effects.map(renderEffect).join('<span class="cc__sep">·</span>');
}

function renderStatsCells(classId: ClassId, domainId: DomainId | null): string {
  const classDef = CLASS_DEFS[classId];
  const stats: Stats = { ...classDef.startingStats };
  const keyStats = new Set<Stat>([classDef.keyStat]);
  if (domainId) {
    const domainDef = DOMAIN_DEFS[domainId];
    stats[domainDef.keyStat] = 8;
    keyStats.add(domainDef.keyStat);
  }
  return STAT_ORDER.map((stat) => {
    const isKey = keyStats.has(stat);
    return `<span class="cc__stat ${isKey ? "is-key" : ""}">${STAT_LABEL[stat]} ${stats[stat]}</span>`;
  }).join("");
}

export class CharacterCreatorScene extends Phaser.Scene {
  private creatorData?: CharCreatorCtx;
  private selectedClass: ClassId | null = null;
  private selectedDomain: DomainId | null = null;
  private variantDom!: Phaser.GameObjects.DOMElement;

  constructor() {
    super("CharacterCreatorScene");
  }

  create() {
    this.creatorData = this.registry.get(REGISTRY_KEYS.currentCharacterCreator) as CharCreatorCtx | null ?? undefined;
    this.variantDom = mountDom(this, this.buildHtml());
    this.attachListeners();
    this.updateUI();
  }

  private buildHtml(): string {
    const renderChip = (cardId: string, count: number): string => {
      const card = CARD_CATALOG[cardId];
      if (!card) return "";
      const badge = count > 1 ? `<span class="cc__chip-count">×${count}</span>` : "";
      return `
        <div class="cc__chip">
          <div class="cc__chip-name-row">
            <span class="cc__chip-name">${card.name}</span>
            ${badge}
          </div>
          <div class="cc__chip-meta">
            <span class="cc__chip-kind">${KIND_LABEL[card.kind]}</span>
            <span class="cc__chip-cost">Стоимость <span class="cc__num">${card.hope.cost}</span></span>
            <span class="cc__chip-formula">${renderEffects(card.hope.effects)}</span>
          </div>
        </div>
      `;
    };

    const renderDomain = (classId: ClassId, domainId: DomainId): string => {
      const classDef = CLASS_DEFS[classId];
      const domainDef = DOMAIN_DEFS[domainId];

      const counts = new Map<string, number>();
      for (const id of classDef.startingClassCards) {
        counts.set(id, (counts.get(id) ?? 0) + 1);
      }
      const chipsHtml = [...counts.entries()].map(([id, n]) => renderChip(id, n)).join("");

      return `
        <div class="cc__domain" data-class="${classId}" data-domain="${domainId}">
          <div class="cc__domain-head">
            <span class="cc__domain-name">${domainDef.name}</span>
            <span class="cc__domain-tagline">${domainDef.description}</span>
            <span class="cc__domain-stat">Ключевой стат: ${STAT_LABEL[domainDef.keyStat]} 8</span>
          </div>
          <div class="cc__domain-deck">${chipsHtml}</div>
        </div>
      `;
    };

    const cardsHtml = CLASS_ORDER.map((classId) => {
      const def = CLASS_DEFS[classId];
      const statsHtml = renderStatsCells(classId, null);

      const domainsHtml = def.domainIds.map((d) => renderDomain(classId, d)).join("");

      return `
        <div class="cc__card" data-class="${classId}">
          <div class="cc__card-head">
            <h2 class="cc__class-name">${def.name}</h2>
            <p class="cc__class-desc">${def.description}</p>
            <div class="cc__stats" data-stats="${classId}">${statsHtml}</div>
          </div>
          <div class="cc__domains">${domainsHtml}</div>
        </div>
      `;
    }).join("");

    return `
      <div class="cc">
        <div class="cc__header">
          <h1 class="cc__title">Создание героя</h1>
          <p class="cc__subtitle">Выбери класс и домен</p>
        </div>
        <div class="cc__body">
          <div class="cc__cards">${cardsHtml}</div>
          <div class="cc__cta">
            <button class="cc__confirm">Начать приключение</button>
            <p class="cc__hint">Сначала выбери класс</p>
          </div>
        </div>
      </div>
    `;
  }

  private attachListeners() {
    const root = this.variantDom.node as HTMLElement;

    root.querySelectorAll(".cc__card").forEach((card) => {
      card.addEventListener("click", () => {
        const classId = (card as HTMLElement).dataset.class as ClassId;
        this.onClassClick(classId);
      });
    });

    root.querySelectorAll(".cc__domain").forEach((domain) => {
      domain.addEventListener("click", (e) => {
        const domainId = (domain as HTMLElement).dataset.domain as DomainId;
        const classId = (domain as HTMLElement).dataset.class as ClassId;
        this.onDomainClick(classId, domainId);
        e.stopPropagation();
      });
    });

    root.querySelector(".cc__confirm")?.addEventListener("click", () => {
      this.onConfirm();
    });

    root.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (target.closest(".cc__card")) return;
      if (target.closest(".cc__confirm")) return;
      this.selectedClass = null;
      this.selectedDomain = null;
      this.updateUI();
    });
  }

  private onClassClick(classId: ClassId) {
    this.selectedClass = classId;
    this.selectedDomain = null;
    this.updateUI();
  }

  private onDomainClick(classId: ClassId, domainId: DomainId) {
    this.selectedClass = classId;
    this.selectedDomain = domainId;
    this.updateUI();
  }

  private updateUI() {
    const root = this.variantDom.node as HTMLElement;

    root.querySelectorAll(".cc__card").forEach((el) => {
      el.classList.toggle("is-selected", (el as HTMLElement).dataset.class === this.selectedClass);
    });

    root.querySelectorAll(".cc__domain").forEach((el) => {
      el.classList.toggle("is-selected", (el as HTMLElement).dataset.domain === this.selectedDomain);
    });

    root.querySelectorAll<HTMLElement>(".cc__stats[data-stats]").forEach((el) => {
      const classId = el.dataset.stats as ClassId;
      const domainId = this.selectedClass === classId ? this.selectedDomain : null;
      el.innerHTML = renderStatsCells(classId, domainId);
    });

    const hint = root.querySelector(".cc__hint") as HTMLElement;
    if (!this.selectedClass) hint.textContent = "Сначала выбери класс";
    else if (!this.selectedDomain) hint.textContent = "Теперь выбери домен";
    else hint.textContent = "Готов начать приключение";
  }

  private onConfirm() {
    if (this.selectedClass === null || this.selectedDomain === null) return;
    this.creatorData?.onChosen(this.selectedClass, this.selectedDomain);
  }

  shutdown() {}
}
